import { useEffect, useRef, useState } from 'react';

/**
 * J2.10 — the notification centre's push transport, client side.
 *
 * The backend chose SSE (see NotificationStream.ts for why). Two consequences shape this hook:
 *
 *   1. `EventSource` CANNOT set an Authorization header. Our sessions are bearer tokens, so the
 *      token travels as a query parameter on this one endpoint. That is a deliberate, bounded
 *      trade — it is a read-only stream of the caller's own notifications, and the alternative
 *      (a cookie session, or a WebSocket with its own hand-off) would mean a second auth path
 *      for one screen. Callers should be aware the token appears in server access logs.
 *
 *   2. The stream is an OPTIMISATION, never the source of truth. The server's `ready` frame says
 *      whether pushes are actually flowing; when they are not — no listener, a proxy that eats
 *      SSE, a browser without EventSource — polling continues at its normal cadence. A push that
 *      never arrives must cost freshness, not correctness.
 *
 * Reconnection is EventSource's own job (the server sends `retry: 5000`), so there is no manual
 * backoff here. What the hook adds is stopping cleanly on unmount and not leaking a listener.
 */

export interface PushedNotification {
  id: string;
  type: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL';
  title: string;
  body: string;
  createdAt: string;
}

const API_BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:3000/api/v1';

export function useNotificationStream(
  onNotification: (n: PushedNotification) => void,
  enabled = true
): { streaming: boolean } {
  const [streaming, setStreaming] = useState(false);
  // Kept in a ref so a caller passing an inline arrow does not tear the connection down and
  // rebuild it on every render — which would reconnect a few times a second.
  const handler = useRef(onNotification);
  handler.current = onNotification;

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const source = new EventSource(
      `${API_BASE_URL}/notifications/stream?access_token=${encodeURIComponent(token)}`
    );

    source.addEventListener('ready', (e) => {
      try {
        const { streaming: live } = JSON.parse((e as MessageEvent).data) as { streaming: boolean };
        setStreaming(live);
      } catch {
        setStreaming(false);
      }
    });
    source.addEventListener('notification', (e) => {
      try {
        handler.current(JSON.parse((e as MessageEvent).data) as PushedNotification);
      } catch {
        /* a malformed frame must not break the stream */
      }
    });
    source.onerror = () => {
      // EventSource retries on its own; all we do is stop claiming to be live, so the caller
      // resumes polling for as long as the connection is down.
      setStreaming(false);
    };

    return () => {
      source.close();
      setStreaming(false);
    };
  }, [enabled]);

  return { streaming };
}
